import { useCallback, useEffect, useRef, useState } from "react";

/**
 * How an attempt to find the browser went.
 *
 * Five outcomes rather than a nullable position, because the four failures need
 * four different things said to the person and two of them are recoverable in
 * different ways. "We could not get your location" covers all of them and helps
 * with none. The same vocabulary the Flutter client uses, so the two apps say
 * the same thing about the same refusal.
 */
export type LocationOutcome =
    | "idle"
    | "locating"
    | "ok"
    | "denied"
    | "unsupported"
    | "unavailable";

/**
 * The answer to one attempt, as the caller sees it.
 *
 * The message travels with the reading rather than being read off the hook
 * afterwards. An `await device.locate()` returns into the closure that was
 * rendered *before* the attempt, where `message` is still whatever it was, so a
 * caller that reads it there reliably shows nothing at the exact moment it has
 * something to say.
 */
export interface LocationReading {
    fix: DeviceFix | null;
    outcome: LocationOutcome;
    /** What to tell the person, or null when there is nothing to say. */
    message: string | null;
}

export interface DeviceFix {
    latitude: number;
    longitude: number;
    /** Radius of the reported position's uncertainty, in metres. */
    accuracyMetres: number;
}

/**
 * How close a fix has to be before it is allowed to name a pick-up.
 *
 * A pick-up is a place a driver has to find at the kerb. A fix good to half a
 * kilometre names the wrong street, and a wrong street is worse than an honest
 * "search for it instead", because the passenger has no way to tell that the
 * address they were shown is not where they are. Matches the Flutter client's
 * threshold.
 */
export const NAMEABLE_ACCURACY_METRES = 150;

/** True when the fix is precise enough to put in an address field. */
export function isPreciseEnoughToName(fix: DeviceFix): boolean {
    return fix.accuracyMetres > 0 && fix.accuracyMetres <= NAMEABLE_ACCURACY_METRES;
}

const MESSAGES: Record<LocationOutcome, string | null> = {
    idle: null,
    locating: null,
    ok: null,
    denied: "We need permission to use your location. Search for the address instead, or allow it and try again.",
    unsupported: "This browser cannot report your location. Search for the address instead.",
    unavailable:
        "We could not get a location just now. Try again, or search for the address.",
};

export interface UseDeviceLocationOptions {
    /**
     * Asks once on mount, at low accuracy, purely to centre a map and bias a
     * search. Off by default: a permission prompt that appears because a page
     * loaded, before the person has asked for anything, is the one most
     * reliably refused.
     */
    watchOnMount?: boolean;
}

/**
 * Where the browser says the person is.
 *
 * Two callers with different needs, so two entry points. `prime` is the quiet
 * one: it is allowed to be refused, says nothing when it is, and exists to
 * centre a map. `locate` is attached to a control the person pressed, wants GPS
 * precision because its answer goes into an address field, and reports why it
 * failed.
 */
export function useDeviceLocation(options: UseDeviceLocationOptions = {}) {
    const { watchOnMount = false } = options;

    const [outcome, setOutcome] = useState<LocationOutcome>("idle");
    const [fix, setFix] = useState<DeviceFix | null>(null);

    // Nothing may call setState after the component has gone: a geolocation
    // callback outlives the component that asked for it whenever the person
    // navigates while the prompt is still up.
    const alive = useRef(true);
    useEffect(() => {
        alive.current = true;
        return () => {
            alive.current = false;
        };
    }, []);

    const read = useCallback(
        (highAccuracy: boolean, announce: boolean) =>
            new Promise<LocationReading>((resolve) => {
                const answer = (
                    fix: DeviceFix | null,
                    outcome: LocationOutcome
                ): LocationReading => ({
                    fix,
                    outcome,
                    message: MESSAGES[outcome],
                });

                if (typeof navigator === "undefined" || !navigator.geolocation) {
                    if (announce && alive.current) setOutcome("unsupported");
                    resolve(answer(null, "unsupported"));
                    return;
                }

                if (announce && alive.current) setOutcome("locating");

                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const next: DeviceFix = {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                            accuracyMetres: position.coords.accuracy,
                        };
                        if (alive.current) {
                            setFix(next);
                            if (announce) setOutcome("ok");
                        }
                        resolve(answer(next, "ok"));
                    },
                    (error) => {
                        const outcome: LocationOutcome =
                            error.code === error.PERMISSION_DENIED
                                ? "denied"
                                : "unavailable";
                        if (announce && alive.current) setOutcome(outcome);
                        resolve(answer(null, outcome));
                    },
                    {
                        enableHighAccuracy: highAccuracy,
                        timeout: highAccuracy ? 15000 : 10000,
                        // A cached fix is fine for centring a map and is not
                        // fine for naming a kerb, so the precise read insists
                        // on a new one.
                        maximumAge: highAccuracy ? 0 : 300000,
                    }
                );
            }),
        []
    );

    /**
     * A quiet, low-accuracy read for centring a map and biasing a search.
     *
     * A refusal is silent: there is no state for "denied" here because nothing
     * behaves differently, the maps simply keep the fallback they would have
     * used.
     */
    const prime = useCallback(() => read(false, false), [read]);

    /** The read behind a control the person pressed. This one reports why. */
    const locate = useCallback(() => read(true, true), [read]);

    /** Puts the notice away once it has been read. */
    const dismiss = useCallback(() => {
        setOutcome((current) =>
            current === "denied" ||
            current === "unavailable" ||
            current === "unsupported"
                ? "idle"
                : current
        );
    }, []);

    useEffect(() => {
        if (!watchOnMount) return;
        void prime();
    }, [watchOnMount, prime]);

    return {
        fix,
        outcome,
        isLocating: outcome === "locating",
        message: MESSAGES[outcome],
        prime,
        locate,
        dismiss,
    };
}

export default useDeviceLocation;
